import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';

import { AiAssistantI18nModule } from '../../assets/i18n/ai-assistant-i18n.module';
import { ChatBlock, ChatMessage, TextBlock, TypewriterReveal } from '../../core/ai-chat';
import { ChatTextBlockComponent } from './blocks/chat-text-block.component';
import { ChatToolBlockComponent } from './blocks/chat-tool-block.component';

const IDLE_REVEAL_ID = '__idle__';

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

/**
 * Dumb, presentational AI chat surface in a generation layout: right-aligned user prompts,
 * full-width bubble-less assistant answers that type in via a view-only typewriter reveal,
 * and a centered empty state. Holds no streaming/accumulation state and has no transport,
 * store, or proto knowledge — a host wires it to a `ChatStateStore` (or use `<ai-chat-connected>`).
 */
@Component({
  selector: 'ai-chat',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    GalaxyAlertModule,
    GalaxyEmptyStateModule,
    GalaxyFormFieldModule,
    GalaxyLoadingSpinnerModule,
    AiAssistantI18nModule,
    ChatTextBlockComponent,
    ChatToolBlockComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ai-chat.component.html',
  styleUrl: './ai-chat.component.scss',
})
export class AiChatComponent {
  readonly messages = input<ChatMessage[]>([]);
  readonly isStreaming = input(false);
  readonly error = input<string | null>(null);

  readonly placeholder = input<string>('');
  readonly emptyStateText = input<string>('');

  readonly send = output<string>();

  protected readonly reducedMotion = prefersReducedMotion();
  protected readonly reveal = new TypewriterReveal({ prefersReducedMotion: this.reducedMotion });

  protected readonly activeAssistant = computed<ChatMessage | null>(() => {
    const msgs = this.messages();
    const last = msgs[msgs.length - 1];
    return last?.role === 'assistant' ? last : null;
  });
  protected readonly activeMessageId = computed(() => this.activeAssistant()?.id ?? null);

  /** The open, still-growing text block (last block of a streaming assistant turn), or null. */
  protected readonly activeTextBlock = computed<TextBlock | null>(() => {
    const m = this.activeAssistant();
    if (!m || m.status !== 'streaming') {
      return null;
    }
    const last = m.blocks[m.blocks.length - 1];
    return last?.kind === 'text' ? last : null;
  });

  /** Most-recently-completed assistant answer, for a single polite announcement. */
  protected readonly lastCompletedAnswer = computed(() => {
    const msgs = this.messages();
    for (let i = msgs.length - 1; i >= 0; i--) {
      const m = msgs[i];
      if (m.role === 'assistant' && m.status === 'complete') {
        return messageText(m);
      }
    }
    return '';
  });

  protected readonly isEmpty = computed(() => this.messages().length === 0);

  /** Id of the most recent user prompt, used to pin a new turn to the top once. */
  protected readonly lastUserMessageId = computed(() => {
    const msgs = this.messages();
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].role === 'user') {
        return msgs[i].id;
      }
    }
    return null;
  });

  /** Reserved space below the transcript so a freshly sent prompt can rest at the top. */
  protected readonly tailSpacerHeight = signal(0);

  protected readonly draft = new FormControl('', { nonNullable: true });

  private readonly scrollContainer = viewChild<ElementRef<HTMLElement>>('scrollContainer');
  private readonly ngZone = inject(NgZone);
  private pinnedUserId: string | null = null;
  private pendingScrollRaf: number | null = null;

  constructor() {
    effect(() => {
      const block = this.activeTextBlock();
      untracked(() => {
        if (block) {
          this.reveal.setState(block.id, block.text, true);
        } else {
          this.reveal.setState(IDLE_REVEAL_ID, '', false);
        }
      });
    });

    afterRenderEffect(() => {
      const msgs = this.messages();
      const streaming = this.isStreaming();
      const lastUserId = this.lastUserMessageId();
      const el = this.scrollContainer()?.nativeElement;
      if (!el) {
        return;
      }

      // Reserve a viewport of space below the transcript while a turn is in flight, so the
      // prompt can sit at the top with its answer generating beneath it.
      const awaitingResponse = msgs.length > 0 && msgs[msgs.length - 1].role === 'user';
      this.tailSpacerHeight.set(streaming || awaitingResponse ? el.clientHeight : 0);

      // Pin each new prompt to the top once; never auto-follow as the answer grows.
      if (lastUserId && lastUserId !== this.pinnedUserId) {
        this.pinnedUserId = lastUserId;
        this.ngZone.runOutsideAngular(() => {
          if (this.pendingScrollRaf !== null) {
            cancelAnimationFrame(this.pendingScrollRaf);
          }
          this.pendingScrollRaf = requestAnimationFrame(() => {
            this.pendingScrollRaf = null;
            const userEl = el.querySelector<HTMLElement>(`[data-msg-id="${lastUserId}"]`);
            if (userEl) {
              el.scrollTop += userEl.getBoundingClientRect().top - el.getBoundingClientRect().top;
            }
          });
        });
      }
    });

    inject(DestroyRef).onDestroy(() => {
      if (this.pendingScrollRaf !== null) {
        cancelAnimationFrame(this.pendingScrollRaf);
      }
      this.reveal.destroy();
    });
  }

  protected isActivelyStreaming(message: ChatMessage): boolean {
    return message.id === this.activeMessageId() && message.status === 'streaming';
  }

  /** Streaming but not currently typing out text: awaiting the first/next tokens or a tool call. */
  protected isThinking(message: ChatMessage): boolean {
    return this.isActivelyStreaming(message) && this.reveal.caughtUp();
  }

  /** Whether this block is the open, typewriter-driven text block of the active streaming turn. */
  protected isActiveTextBlock(block: ChatBlock): boolean {
    const active = this.activeTextBlock();
    return !!active && block.kind === 'text' && block.id === active.id;
  }

  /** Text to render for a text block: the typewriter prefix while active, otherwise the full text. */
  protected blockText(block: TextBlock): string {
    return this.isActiveTextBlock(block) ? this.reveal.revealedText() : block.text;
  }

  /** Flattened text of a user turn, rendered inside the prompt bubble. */
  protected userText(message: ChatMessage): string {
    return messageText(message);
  }

  protected onEnter(event: Event): void {
    if ((event as KeyboardEvent).shiftKey) {
      return;
    }
    event.preventDefault();
    this.submit();
  }

  protected submit(): void {
    const text = this.draft.value.trim();
    if (!text || this.isStreaming()) {
      return;
    }
    this.send.emit(text);
    this.draft.setValue('');
  }
}

function messageText(message: ChatMessage): string {
  return message.blocks
    .filter((b) => b.kind === 'text')
    .map((b) => (b.kind === 'text' ? b.text : ''))
    .join('');
}
